// Shorts.jsx (fixed — entire component)
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo
} from "react";
import { createPortal } from "react-dom";
import "./Short.css";

/* ============================
   Configuration (move secrets to env in production)
   ============================ */
const YOUTUBE_API_KEYS = [
  "AIzaSyCVxFmqpRsZjjX5O0fXFSQUuA2im6yItE8",
  "AIzaSyCwlSfBHqCIN647mbPku4-tkWDa0RaUT2w"
];
let currentKeyIndex = 0;
const getNextAPIKey = () => {
  const k = YOUTUBE_API_KEYS[currentKeyIndex];
  currentKeyIndex = (currentKeyIndex + 1) % YOUTUBE_API_KEYS.length;
  return k;
};

const INITIAL_SHORTS_IDS = [
  "wWoSx73g1DI",
  "0YxKnwiEZsk",
  "R5dBxy4lNbM",
  "j4vAqS8FUtw",
  "yJf7jmq1jrs",
  "4j3Hufxjb6w",
  "pIZUyyun3eg"
];

const STORAGE_KEYS = {
  VIDEO_QUEUE: "shorts_videoQueue_v4",
  CURRENT_VIDEO_ID: "shorts_currentVideoId_v4",
  CURRENT_VIDEO_INDEX: "shorts_currentIndex_v4",
  VIDEO_PROGRESS: "shorts_videoProgress_v4",
  WATCHED_VIDEOS: "watchedShorts_v4",
  LAST_FETCH_CONFIG: "shorts_lastFetchConfig_v4",
  VIDEO_TITLES: "shorts_videoTitles_v4",
  INITIAL_SHORTS_SHOWN: "shorts_initialShortsShown_v4",
  USER_CUSTOMIZED: "shorts_userCustomized_v4",
  SHUFFLED_INITIAL_SHORTS: "shorts_shuffledInitialShorts_v4"
};

/* ============================
   Utilities
   ============================ */
const debounce = (fn, ms) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
};

const parseVideoIdFromSrc = (src = "") => {
  try {
    if (!src) return null;
    let m = src.match(/\/embed\/([^?&/]+)/);
    if (m) return m[1];
    m = src.match(/[?&]v=([^&]+)/);
    if (m) return m[1];
    m = src.match(/\/vi\/([^/]+)\//) || src.match(/\/v\/([^/]+)/);
    if (m) return m[1];
    const parts = src.split("/");
    return parts[parts.length - 1].split("?")[0] || null;
  } catch {
    return null;
  }
};

const normalizeEntry = (entry) => {
  if (!entry) return null;
  if (typeof entry === "string") {
    const id = entry;
    return {
      id,
      title: "",
      thumbnail: `https://img.youtube.com/vi/${id}/hqdefault.jpg`,
      src: `https://www.youtube.com/embed/${id}?rel=0`,
      duration: ""
    };
  }
  const id =
    entry.id ||
    entry.videoId ||
    parseVideoIdFromSrc(entry.src) ||
    parseVideoIdFromSrc(entry.thumbnail) ||
    null;
  if (!id) return null;

  return {
    id,
    title: entry.title || "",
    thumbnail: entry.thumbnail || `https://img.youtube.com/vi/${id}/hqdefault.jpg`,
    src: entry.src || `https://www.youtube.com/embed/${id}?rel=0`,
    duration: entry.duration || ""
  };
};

const shuffleArray = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};
/* ============================
   Storage helpers
   ============================ */
const saveVideoQueue = (videos) => {
  try {
    localStorage.setItem(STORAGE_KEYS.VIDEO_QUEUE, JSON.stringify(videos));
  } catch {}
};
const loadVideoQueue = () => {
  try {
    const s = localStorage.getItem(STORAGE_KEYS.VIDEO_QUEUE);
    return s ? JSON.parse(s) : null;
  } catch {
    return null;
  }
};

const saveCurrentVideoId = debounce((id) => {
  try {
    if (id) localStorage.setItem(STORAGE_KEYS.CURRENT_VIDEO_ID, id);
  } catch {}
}, 500);
const loadCurrentVideoId = () => {
  try {
    return localStorage.getItem(STORAGE_KEYS.CURRENT_VIDEO_ID);
  } catch {
    return null;
  }
};
const saveCurrentIndex = debounce((i) => {
  try {
    if (typeof i === "number")
      localStorage.setItem(STORAGE_KEYS.CURRENT_VIDEO_INDEX, String(i));
  } catch {}
}, 500);
const loadCurrentIndex = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEYS.CURRENT_VIDEO_INDEX);
    return v === null ? null : Number(v);
  } catch {
    return null;
  }
};
const saveVideoProgress = debounce((m) => {
  try {
    localStorage.setItem(STORAGE_KEYS.VIDEO_PROGRESS, JSON.stringify(m));
  } catch {}
}, 1000);
const loadVideoProgress = () => {
  try {
    const s = localStorage.getItem(STORAGE_KEYS.VIDEO_PROGRESS);
    return s ? JSON.parse(s) : {};
  } catch {
    return {};
  }
};
const saveFetchConfig = (cfg) => {
  try {
    localStorage.setItem(STORAGE_KEYS.LAST_FETCH_CONFIG, JSON.stringify(cfg));
  } catch {}
};
const loadFetchConfig = () => {
  try {
    const s = localStorage.getItem(STORAGE_KEYS.LAST_FETCH_CONFIG);
    return s ? JSON.parse(s) : null;
  } catch {
    return null;
  }
};
const loadVideoTitles = () => {
  try {
    const s = localStorage.getItem(STORAGE_KEYS.VIDEO_TITLES);
    return s ? JSON.parse(s) : {};
  } catch {
    return {};
  }
};
const saveVideoTitles = debounce((t) => {
  try {
    localStorage.setItem(STORAGE_KEYS.VIDEO_TITLES, JSON.stringify(t));
  } catch {}
}, 2000);
const getInitialShortsShown = () => {
  try {
    return localStorage.getItem(STORAGE_KEYS.INITIAL_SHORTS_SHOWN) === "true";
  } catch {
    return false;
  }
};
const setInitialShortsShown = (v) => {
  try {
    localStorage.setItem(STORAGE_KEYS.INITIAL_SHORTS_SHOWN, v ? "true" : "false");
  } catch {}
};
const getUserCustomized = () => {
  try {
    return localStorage.getItem(STORAGE_KEYS.USER_CUSTOMIZED) === "true";
  } catch {
    return false;
  }
};
const setUserCustomized = (v) => {
  try {
    localStorage.setItem(STORAGE_KEYS.USER_CUSTOMIZED, v ? "true" : "false");
  } catch {}
};
const getShuffledInitialShorts = () => {
  try {
    const s = localStorage.getItem(STORAGE_KEYS.SHUFFLED_INITIAL_SHORTS);
    if (s) return JSON.parse(s);
    const sh = shuffleArray([...INITIAL_SHORTS_IDS]);
    localStorage.setItem(STORAGE_KEYS.SHUFFLED_INITIAL_SHORTS, JSON.stringify(sh));
    return sh;
  } catch {
    return INITIAL_SHORTS_IDS;
  }
};

/* ============================
   YouTube API readiness hook
   ============================ */
function useYouTubeAPIReady() {
  const [ready, setReady] = useState(!!(typeof window !== "undefined" && window.YT && window.YT.Player));
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.YT && window.YT.Player) {
      setReady(true);
      return;
    }
    const onEvent = () => {
      if (window.YT && window.YT.Player) {
        setReady(true);
        window.removeEventListener("youtube-api-ready", onEvent);
      }
    };
    window.addEventListener("youtube-api-ready", onEvent);
    if (!document.querySelector("#yt-api-script")) {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      tag.id = "yt-api-script";
      document.body.appendChild(tag);
    }
    window.onYouTubeIframeAPIReady = () => window.dispatchEvent(new Event("youtube-api-ready"));
    return () => window.removeEventListener("youtube-api-ready", onEvent);
  }, []);
  return ready;
}

/* ============================
   Component
   ============================ */
export default function Shorts() {
  /* ---------- refs & state ---------- */
  const isMountedRef = useRef(true);
  useEffect(() => { isMountedRef.current = true; return () => { isMountedRef.current = false; }; }, []);

  const [videos, setVideos] = useState([]);
  const videosRef = useRef(videos);
  useEffect(() => { videosRef.current = videos; }, [videos]);

  const [isFetching, setIsFetching] = useState(true);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const currentIndexRef = useRef(currentIndex);
  useEffect(() => { currentIndexRef.current = currentIndex; }, [currentIndex]);

  const [videoTitles, setVideoTitles] = useState(loadVideoTitles());
  const videoTitlesRef = useRef(videoTitles);
  useEffect(() => { videoTitlesRef.current = videoTitles; }, [videoTitles]);

  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  const playerSlots = useRef([null, null, null]);
  const videoSlots = useRef([0, 1, 2]);
  const currentSlot = useRef(0);
  const slotRefs = useRef([null, null, null]);
  const setSlotRef = (i) => (el) => { slotRefs.current[i] = el; };
  const iframeNodeRefs = useRef([null, null, null]);
  const setIframeRef = (i) => (el) => { iframeNodeRefs.current[i] = el; };
  const thumbVisible = useRef([true, true, true]);

  const containerRef = useRef(null);
  const overlayRef = useRef(null);
  const globalProgressRef = useRef(null);
  const globalProgressBarRef = useRef(null);
  const progressThumbRef = useRef(null);

  const videoProgressMap = useRef(loadVideoProgress());
  const progressFrameRef = useRef(null);

  // drag & touch
  const touchStartY = useRef(0);
  const isDragging = useRef(false);
  const dragStartTime = useRef(0);
  const dragDeltaY = useRef(0);
  const rafId = useRef(null);

  const isDraggingProgress = useRef(false);
  const wasPlayingBeforeScrub = useRef(false);

  const watchedVideosBuffer = useRef(new Set());
  const recentlyFetchedIds = useRef(new Set());
  const titleFetchInProgress = useRef(false);

  const apiReady = useYouTubeAPIReady();

  const userLanguage = localStorage.getItem("userLanguage") || "English";

  const getCurrentCategory = useCallback(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("vitstraCategory") || '"all"');
      return Array.isArray(stored) ? stored.map(s => String(s).toLowerCase()) : [String(stored).toLowerCase()];
    } catch {
      return ["all"];
    }
  }, []);

  const categoriesEqual = useCallback((a = [], b = []) => {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    const sa = a.map(x => String(x).toLowerCase()).sort();
    const sb = b.map(x => String(x).toLowerCase()).sort();
    return sa.every((v, i) => v === sb[i]);
  }, []);

  /* ---------- watched videos helpers ---------- */
  const getWatchedVideos = useCallback(() => {
    try {
      const w = localStorage.getItem(STORAGE_KEYS.WATCHED_VIDEOS);
      return w ? JSON.parse(w) : [];
    } catch {
      return [];
    }
  }, []);

  const saveWatchedVideos = useCallback(() => {
    if (watchedVideosBuffer.current.size === 0) return;
    try {
      const existing = getWatchedVideos();
      const merged = [...new Set([...existing, ...Array.from(watchedVideosBuffer.current)])];
      localStorage.setItem(STORAGE_KEYS.WATCHED_VIDEOS, JSON.stringify(merged));
      watchedVideosBuffer.current.clear();
    } catch (e) {}
  }, [getWatchedVideos]);

  const resetWatchedVideos = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEYS.WATCHED_VIDEOS);
      watchedVideosBuffer.current.clear();
    } catch (e) {}
  }, []);

  /* ---------- Save current state ---------- */
  const saveCurrentState = useCallback(() => {
    try {
      if (!videosRef.current || videosRef.current.length === 0) return;
      const currentVideoIndex = videoSlots.current[currentSlot.current];
      const currentVideo = videosRef.current[currentVideoIndex];
      if (currentVideo?.id) {
        saveCurrentVideoId(currentVideo.id);
      }
      saveCurrentIndex(currentVideoIndex);
      saveVideoProgress(videoProgressMap.current);
      saveWatchedVideos();
      saveVideoQueue(videosRef.current);
    } catch {}
  }, [saveWatchedVideos]);

  useEffect(() => {
    return () => {
      saveCurrentState();
      saveVideoTitles(videoTitlesRef.current);
    };
  }, [saveCurrentState]);

  /* ---------- Interaction detection ---------- */
  useEffect(() => {
    const handler = () => {
      setHasUserInteracted(true);
      document.removeEventListener("click", handler);
      document.removeEventListener("touchstart", handler);
    };
    document.addEventListener("click", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("click", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, []);

  /* ============================
     Platform shorts loader
     ============================ */
  const loadPlatformShorts = useCallback(
    async ({ categories = ["all"], forceRefresh = false } = {}) => {
      if (isMountedRef.current === false) return [];
      const language = (userLanguage || "English").toLowerCase();
      const languages = language === "both" ? ["English", "Hindi"] : [language.charAt(0).toUpperCase() + language.slice(1)];
      const type = "Shorts";

      const watched = getWatchedVideos();

      let allShorts = [];
      for (const lang of languages) {
        const path = `/API/Shorts/${lang}${type}.json`;
        try {
          const res = await fetch(path, { headers: { Accept: "application/json" } });
          if (!res.ok) continue;
          const text = await res.text();
          if (!text) continue;
          const data = JSON.parse(text);
          if (!data || typeof data !== "object") continue;
          const normalizedObj = {};
          Object.keys(data).forEach((k) => (normalizedObj[k.toLowerCase()] = Array.isArray(data[k]) ? data[k] : []));
          let shorts = [];
          if (categories.includes("all")) {
            Object.values(normalizedObj).forEach((arr) => shorts.push(...arr));
          } else {
            categories.forEach((cat) => {
              if (normalizedObj[cat]) shorts.push(...normalizedObj[cat]);
            });
          }
          const normalizedEntries = shorts.map(normalizeEntry).filter(Boolean).filter((s) => s.id && !watched.includes(s.id));
          allShorts.push(...normalizedEntries);
        } catch (err) {
          console.error("Error loading platform shorts:", err);
        }
      }

      if (allShorts.length === 0) {
        // try after clearing watched
        resetWatchedVideos();
        for (const lang of languages) {
          const path = `/API/Shorts/${lang}${type}.json`;
          try {
            const res = await fetch(path, { headers: { Accept: "application/json" } });
            if (!res.ok) continue;
            const text = await res.text();
            if (!text) continue;
            const data = JSON.parse(text);
            const normalizedObj = {};
            Object.keys(data).forEach((k) => (normalizedObj[k.toLowerCase()] = Array.isArray(data[k]) ? data[k] : []));
            if (categories.includes("all")) {
              Object.values(normalizedObj).forEach((arr) => allShorts.push(...arr.map(normalizeEntry).filter(Boolean)));
            } else {
              categories.forEach((cat) => {
                if (normalizedObj[cat]) allShorts.push(...normalizedObj[cat].map(normalizeEntry).filter(Boolean));
              });
            }
          } catch (err) {}
        }
      }

      if (allShorts.length > 0) {
        const seen = new Set();
        const uniq = [];
        for (const s of allShorts) {
          if (!seen.has(s.id)) { seen.add(s.id); uniq.push(s); }
        }
        return shuffleArray(uniq);
      }
      return [];
    },
    [userLanguage, getWatchedVideos, resetWatchedVideos]
  );

  /* ============================
     Titles fetcher
     ============================ */
  const fetchTitlesForIds = useCallback(async (ids) => {
    if (!ids || ids.length === 0) return;
    if (titleFetchInProgress.current) return;
    titleFetchInProgress.current = true;
    try {
      const noembedResults = {};
      await Promise.all(ids.map(async (id) => {
        try {
          const r = await fetch(`https://noembed.com/embed?url=https://www.youtube.com/watch?v=${id}`);
          if (r.ok) {
            const j = await r.json();
            if (j && j.title) noembedResults[id] = j.title;
          }
        } catch {}
      }));

      const newTitles = { ...videoTitlesRef.current, ...noembedResults };

      const still = ids.filter(id => !newTitles[id]);
      const chunkSize = 12;
      for (let i = 0; i < still.length; i += chunkSize) {
        const chunk = still.slice(i, i + chunkSize);
        chunk.forEach(id => recentlyFetchedIds.current.add(id));

        let success = false;
        for (let attempt = 0; attempt < 3 && !success; attempt++) {
          try {
            const apiKey = getNextAPIKey();
            const url =
              `https://youtube.googleapis.com/youtube/v3/videos?part=snippet&id=${chunk.join(",")}&key=${apiKey}`;
            const resp = await fetch(url);
            if (resp.ok) {
              const data = await resp.json();
              data.items?.forEach(it => {
                if (it?.id && it.snippet?.title) newTitles[it.id] = it.snippet.title;
              });
              success = true;
              break;
            } else {
              if (resp.status === 403 || resp.status === 429) {
                console.warn("YouTube API quota/limit hit:", resp.status);
                break;
              }
            }
          } catch (e) {
            const waitTime = 500 * Math.pow(2, attempt);
            await new Promise(r => setTimeout(r, waitTime));
          }
        }

        // fallback labels
        chunk.forEach((id) => {
          if (!newTitles[id]) newTitles[id] = `Video • ${id}`;
        });

        setTimeout(() => {
          chunk.forEach(id => recentlyFetchedIds.current.delete(id));
        }, 30 * 1000);
      }

      if (Object.keys(newTitles).length > Object.keys(videoTitlesRef.current).length) {
        setVideoTitles(newTitles);
        saveVideoTitles(newTitles);
      }
    } finally {
      titleFetchInProgress.current = false;
    }
  }, []);

  /* ============================
     Player control & progress
     ============================ */
  const stopProgressInterval = useCallback(() => {
    if (progressFrameRef.current) {
      cancelAnimationFrame(progressFrameRef.current);
      progressFrameRef.current = null;
    }
  }, []);

  const startProgressInterval = useCallback((slotIndex, videoId) => {
    if (!isMountedRef.current) return;
    stopProgressInterval();
    const player = playerSlots.current[slotIndex];
    if (!player || typeof player.getDuration !== "function") return;

    let lastPct = -1;
    const loop = () => {
      if (!isMountedRef.current) {
        stopProgressInterval();
        return;
      }
      try {
        if (document.hidden || currentSlot.current !== slotIndex) {
          stopProgressInterval();
          return;
        }
        const duration = player.getDuration() || 0;
        const current = player.getCurrentTime?.() || 0;
        if (!duration || isNaN(duration)) {
          progressFrameRef.current = requestAnimationFrame(loop);
          return;
        }
        if (videoId) {
          videoProgressMap.current[videoId] = current;
        }
        const pct = Math.min((current / duration) * 100, 100);
        if (Math.abs(pct - lastPct) >= 0.5 || pct === 100) {
          const bar = globalProgressBarRef.current;
          const thumb = progressThumbRef.current;
          if (bar && !isDraggingProgress.current) bar.style.width = `${pct}%`;
          if (thumb && !isDraggingProgress.current) thumb.style.left = `${pct}%`;
          lastPct = pct;
        }
        if (pct >= 95 && videoId && !watchedVideosBuffer.current.has(videoId)) {
          watchedVideosBuffer.current.add(videoId);
        }
        progressFrameRef.current = requestAnimationFrame(loop);
      } catch (err) {
        stopProgressInterval();
      }
    };
    progressFrameRef.current = requestAnimationFrame(loop);
  }, [stopProgressInterval]);

  const pausePlayer = useCallback((slotIndex) => {
    const p = playerSlots.current[slotIndex];
    if (!p) return;
    try {
      const state = typeof p.getPlayerState === "function" ? p.getPlayerState() : null;
      const Y = window?.YT;
      if (state === Y?.PlayerState?.PLAYING || state === Y?.PlayerState?.BUFFERING) {
        if (typeof p.pauseVideo === "function") p.pauseVideo();
      }
    } catch (e) {}
  }, []);

  const playPlayer = useCallback((slotIndex) => {
    const p = playerSlots.current[slotIndex];
    if (!p) return;
    try {
      const state = typeof p.getPlayerState === "function" ? p.getPlayerState() : null;
      const Y = window?.YT;
      if (state !== Y?.PlayerState?.PLAYING) {
        if (typeof p.playVideo === "function") p.playVideo();
      }
    } catch (e) {}
  }, []);

  /* ---------- loadVideoIntoSlot: cue or load into YT player ---------- */
  const loadVideoIntoSlot = useCallback((slotIndex, videoIndex, playWhenReady = false) => {
    if (!isMountedRef.current) return;
    if (videoIndex < 0 || videoIndex >= videosRef.current.length) {
      videoSlots.current[slotIndex] = -1;
      return;
    }

    const p = playerSlots.current[slotIndex];
    const vidObj = videosRef.current[videoIndex];
    const videoId = vidObj?.id || vidObj;

    try {
      const bar = globalProgressBarRef.current;
      const thumb = progressThumbRef.current;
      if (bar && !isDraggingProgress.current) bar.style.width = "0%";
      if (thumb && !isDraggingProgress.current) thumb.style.left = "0%";
    } catch {}

    videoSlots.current[slotIndex] = videoIndex;
    thumbVisible.current[slotIndex] = true;

    if (!p || !videoId) return;

    try {
      const savedProgress = videoProgressMap.current[videoId] || 0;
      if (slotIndex === currentSlot.current && playWhenReady) {
        if (typeof p.loadVideoById === "function") {
          p.loadVideoById({ videoId, startSeconds: savedProgress });
        } else {
          p.cueVideoById?.({ videoId, startSeconds: savedProgress });
        }
      } else {
        if (typeof p.cueVideoById === "function") {
          p.cueVideoById({ videoId, startSeconds: savedProgress });
        } else {
          p.loadVideoById?.({ videoId, startSeconds: savedProgress });
          p.pauseVideo?.();
        }
      }
    } catch (err) {}
  }, []);

  /* ---------- destroyPlayers ---------- */
  const destroyPlayers = useCallback(() => {
    try {
      for (let i = 0; i < 3; i++) {
        const p = playerSlots.current[i];
        if (p && typeof p.destroy === "function") {
          try { p.destroy(); } catch (e) {}
        }
        playerSlots.current[i] = null;
      }
    } catch (err) {}
  }, []);

  /* ---------- initializePlayers ---------- */
  const initializePlayers = useCallback(() => {
    if (!isMountedRef.current) return;

    const YT = window?.YT;
    if (!YT || !YT.Player) return;
    if (!videosRef.current || videosRef.current.length === 0) return;

    const allRefsExist = iframeNodeRefs.current.every((ref) => ref instanceof Element);
    if (!allRefsExist) return;

    destroyPlayers();

    for (let i = 0; i < 3; i++) {
      const container = iframeNodeRefs.current[i];
      if (!container) continue;

      const initialVideoIndex =
        videoSlots.current[i] !== undefined && videoSlots.current[i] >= 0
          ? videoSlots.current[i]
          : Math.min(i, videosRef.current.length - 1);
      const initialVideoId = videosRef.current[initialVideoIndex]?.id || videosRef.current[initialVideoIndex];

      try {
        playerSlots.current[i] = new YT.Player(container, {
          videoId: initialVideoId,
          playerVars: {
            autoplay: 0,
            controls: 0,
            modestbranding: 1,
            rel: 0,
            playsinline: 1,
            fs: 1,
            iv_load_policy: 3,
            enablejsapi: 1
          },
          events: {
            onReady: (evt) => {
              if (!isMountedRef.current) return;
              thumbVisible.current[i] = true;

              if (videoSlots.current[i] === undefined || videoSlots.current[i] === null) {
                videoSlots.current[i] = initialVideoIndex;
              }

              if (i === currentSlot.current) {
                const vidIndex = videoSlots.current[i];
                const videoId = videosRef.current[vidIndex]?.id || videosRef.current[vidIndex];
                const savedProgress = videoProgressMap.current[videoId] || 0;
                try {
                  evt.target.loadVideoById({ videoId, startSeconds: savedProgress });
                  setTimeout(() => { thumbVisible.current[i] = false; }, 150);
                } catch {
                  evt.target.cueVideoById?.({ videoId, startSeconds: savedProgress });
                  evt.target.playVideo?.();
                }
              } else {
                const vidIndex = videoSlots.current[i];
                const videoId = videosRef.current[vidIndex]?.id || videosRef.current[vidIndex];
                try {
                  evt.target.cueVideoById?.({ videoId, startSeconds: 0 });
                } catch {
                  evt.target.loadVideoById?.({ videoId, startSeconds: 0 });
                  evt.target.pauseVideo?.();
                }
              }
            },
            onStateChange: (ev) => {
              if (!isMountedRef.current) return;
              const slotIndex = playerSlots.current.indexOf(ev.target);
              if (slotIndex === -1) return;
              const Y = YT;

              if (ev.data === Y.PlayerState.ENDED) {
                const mapped = videoSlots.current[slotIndex];
                const vObj = videosRef.current[mapped];
                if (vObj?.id) {
                  watchedVideosBuffer.current.add(vObj.id);
                  delete videoProgressMap.current[vObj.id];
                }
                stopProgressInterval();
                try { ev.target.seekTo(0); ev.target.playVideo(); } catch {}
                const bar = globalProgressBarRef.current;
                const thumb = progressThumbRef.current;
                if (bar && !isDraggingProgress.current) bar.style.width = "0%";
                if (thumb && !isDraggingProgress.current) thumb.style.left = "0%";
              } else if (ev.data === Y.PlayerState.PLAYING) {
                for (let j = 0; j < 3; j++) { if (j !== slotIndex) pausePlayer(j); }
                if (currentSlot.current === slotIndex) {
                  const vidIdx = videoSlots.current[slotIndex];
                  const vidObj = videosRef.current[vidIdx];
                  startProgressInterval(slotIndex, vidObj?.id || vidObj);
                }
              } else if (ev.data === Y.PlayerState.PAUSED) {
                if (currentSlot.current === slotIndex) stopProgressInterval();
              }
            },
            onError: (err) => { /* ignore gracefully */ }
          }
        });
      } catch (err) {}
    }
  }, [destroyPlayers, pausePlayer, startProgressInterval, stopProgressInterval]);

  /* ---------- handleTap (play/pause) ---------- */
  const handleTap = useCallback((slotIndex) => {
    try {
      const p = playerSlots.current[slotIndex];
      if (p && typeof p.getPlayerState === "function") {
        const state = p.getPlayerState?.();
        const Y = window?.YT;
        if (state === Y?.PlayerState?.PLAYING) {
          pausePlayer(slotIndex);
          stopProgressInterval();
        } else {
          playPlayer(slotIndex);
          const vidObj = videosRef.current[videoSlots.current[slotIndex]];
          startProgressInterval(slotIndex, vidObj?.id || vidObj);
        }
        return;
      }
      initializePlayers();
      setTimeout(() => {
        const p2 = playerSlots.current[slotIndex];
        if (p2 && typeof p2.playVideo === "function") {
          p2.playVideo();
          const vidObj = videosRef.current[videoSlots.current[slotIndex]];
          startProgressInterval(slotIndex, vidObj?.id || vidObj);
        }
      }, 300);
    } catch (e) {}
  }, [initializePlayers, pausePlayer, playPlayer, startProgressInterval, stopProgressInterval]);

  /* ============================
     Navigation (index change + preloading)
     ============================ */
  const loadPlatformIfApproachingEnd = useCallback((newIndex) => {
    const shuffledInitial = getShuffledInitialShorts();
    const initialShortsShown = getInitialShortsShown();
    // only attempt prefetch if the initial sequence hasn't been marked shown
    if (!initialShortsShown && newIndex >= shuffledInitial.length - 3) {
      // don't await: best-effort prefetch
      loadPlatformShorts({ categories: getCurrentCategory() }).then((loaded) => {
        if (loaded && loaded.length > 0) {
          const initialIds = new Set(shuffledInitial);
          const filtered = loaded.filter(it => !initialIds.has(it.id));

          let updatedVideos;
          const userCustomized = getUserCustomized();
          if (userCustomized) updatedVideos = filtered;
          else updatedVideos = [...shuffledInitial.map(normalizeEntry).filter(Boolean), ...filtered];

          setVideos((prev) => {
            const prevIds = new Set((prev || []).map(v => v.id));
            const newOnes = updatedVideos.filter(v => !prevIds.has(v.id));
            if (newOnes.length === 0) return prev;
            const merged = [...(prev || []), ...newOnes];
            saveVideoQueue(merged);
            return merged;
          });

          // mark initial shown only when we've appended platform results
          if (!getUserCustomized()) setInitialShortsShown(true);
        }
      }).catch(() => {});
    }
  }, [loadPlatformShorts, getCurrentCategory]);

  const navigateToIndex = useCallback((newIndex) => {
    if (!videosRef.current || videosRef.current.length === 0) return;
    if (newIndex < 0 || newIndex >= videosRef.current.length) return;
    if (newIndex === currentIndexRef.current) return;

    loadPlatformIfApproachingEnd(newIndex);

    const directionDown = newIndex > currentIndexRef.current ? 1 : -1;
    const oldSlot = currentSlot.current;

    pausePlayer(oldSlot);
    stopProgressInterval();

    const oldVideoIndex = videoSlots.current[oldSlot];
    const oldVideo = videosRef.current[oldVideoIndex];
    if (oldVideo?.id) saveCurrentVideoId(oldVideo.id);

    const nextSlot = (currentSlot.current + (directionDown > 0 ? 1 : 2)) % 3;
    currentSlot.current = nextSlot;
    videoSlots.current[currentSlot.current] = newIndex;
    thumbVisible.current[currentSlot.current] = true;

    const prevIndex = newIndex - 1;
    const nextIndex = newIndex + 1;
    const behindSlot = (currentSlot.current + 1) % 3;
    const oppositeSlot = (currentSlot.current + 2) % 3;

    if (prevIndex >= 0) loadVideoIntoSlot(oppositeSlot, prevIndex, false);
    else videoSlots.current[oppositeSlot] = -1;

    if (nextIndex < videosRef.current.length) loadVideoIntoSlot(behindSlot, nextIndex, false);
    else videoSlots.current[behindSlot] = -1;

    setCurrentIndex(newIndex);
    saveCurrentIndex(newIndex);

    const newVideo = videosRef.current[newIndex];
    if (newVideo?.id) saveCurrentVideoId(newVideo.id);

    setTimeout(() => {
      const activeSlot = currentSlot.current;
      loadVideoIntoSlot(activeSlot, newIndex, true);
      setTimeout(() => { thumbVisible.current[activeSlot] = false; }, 150);
      setTimeout(() => {
        const vidObj = videosRef.current[videoSlots.current[activeSlot]];
        startProgressInterval(activeSlot, vidObj?.id || vidObj);
      }, 220);
    }, 360);
  }, [loadVideoIntoSlot, pausePlayer, startProgressInterval, stopProgressInterval, loadPlatformIfApproachingEnd]);

  /* ============================
     Drag & touch handlers (UI)
     ============================ */
  const applySlotTransformsDirect = useCallback((deltaY) => {
    const h = window.innerHeight || 800;
    const pct = (deltaY / h) * 100;
    for (let slotIndex = 0; slotIndex < 3; slotIndex++) {
      const el = slotRefs.current[slotIndex];
      if (!el) continue;
      const rel = (slotIndex - currentSlot.current + 3) % 3;
      const basePct = rel === 0 ? 0 : rel === 1 ? 100 : -100;
      const finalPct = basePct + pct;
      el.style.transition = "none";
      el.style.transform = `translate3d(0, ${finalPct}%, 0)`;
      el.style.zIndex = rel === 0 ? 70 : 60;
      el.style.opacity = rel === 0 ? "1" : "0.985";
    }
  }, []);

  const resetSlotTransformsAnimated = useCallback(() => {
    const duration = 360;
    const easing = "cubic-bezier(0.22,1,0.36,1)";
    for (let slotIndex = 0; slotIndex < 3; slotIndex++) {
      const el = slotRefs.current[slotIndex];
      if (!el) continue;
      el.style.transition = `transform ${duration}ms ${easing}, opacity 220ms linear`;

      const rel = (slotIndex - currentSlot.current + 3) % 3;
      const basePct = rel === 0 ? 0 : rel === 1 ? 100 : -100;
      el.style.transform = `translate3d(0, ${basePct}%, 0)`;
      el.style.opacity = rel === 0 ? "1" : "0.985";
    }
    setTimeout(() => {
      for (let s = 0; s < 3; s++) {
        const el = slotRefs.current[s];
        if (!el) continue;
        el.style.transition = "";
      }
    }, duration + 40);
  }, []);

  const performNavigateOrReset = useCallback((deltaY, timeDelta) => {
    const h = window.innerHeight || 800;
    const threshold = h * 0.12;
    const velocity = Math.abs(deltaY) / Math.max(1, timeDelta);
    const fastSwipe = velocity > 0.6;
    const reached = Math.abs(deltaY) > threshold;

    if (deltaY > 0 && currentIndexRef.current === 0) {
      resetSlotTransformsAnimated();
      return;
    }

    if ((deltaY > 0 && (reached || fastSwipe)) && currentIndexRef.current > 0) {
      navigateToIndex(currentIndexRef.current - 1);
    } else if ((deltaY < 0 && (reached || fastSwipe)) && currentIndexRef.current < videosRef.current.length - 1) {
      navigateToIndex(currentIndexRef.current + 1);
    } else {
      resetSlotTransformsAnimated();
    }
  }, [navigateToIndex, resetSlotTransformsAnimated]);

  /* ---------- Progress scrub handlers ---------- */
  const handleProgressDragStart = useCallback((e) => {
    e.stopPropagation(); e.preventDefault();
    isDraggingProgress.current = true;
    const player = playerSlots.current[currentSlot.current];
    if (player && typeof player.getPlayerState === "function") {
      const state = player.getPlayerState();
      wasPlayingBeforeScrub.current = state === window?.YT?.PlayerState?.PLAYING;
      if (wasPlayingBeforeScrub.current) {
        pausePlayer(currentSlot.current);
        stopProgressInterval();
      }
    }

    const rect = globalProgressRef.current?.getBoundingClientRect();
    if (!rect) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const x = clientX - rect.left;
    const pct = Math.max(0, Math.min(100, (x / rect.width) * 100));
    const bar = globalProgressBarRef.current;
    const thumb = progressThumbRef.current;
    if (bar) { bar.style.transition = "none"; bar.style.width = `${pct}%`; }
    if (thumb) { thumb.style.transition = "none"; thumb.style.left = `${pct}%`; }

    if (player && typeof player.getDuration === "function") {
      const duration = player.getDuration();
      const seekTime = (pct / 100) * duration;
      try {
        player.seekTo(seekTime, true);
        const vidIdx = videoSlots.current[currentSlot.current];
        const videoId = videosRef.current[vidIdx]?.id || videosRef.current[vidIdx];
        if (videoId) videoProgressMap.current[videoId] = seekTime;
      } catch (e) {}
    }
  }, [pausePlayer, stopProgressInterval]);

  const handleProgressDragMove = useCallback((e) => {
    if (!isDraggingProgress.current) return;
    e.stopPropagation(); e.preventDefault();
    const rect = globalProgressRef.current?.getBoundingClientRect();
    if (!rect) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const x = clientX - rect.left;
    const pct = Math.max(0, Math.min(100, (x / rect.width) * 100));
    const bar = globalProgressBarRef.current;
    const thumb = progressThumbRef.current;
    if (bar) { bar.style.transition = "none"; bar.style.width = `${pct}%`; }
    if (thumb) { thumb.style.transition = "none"; thumb.style.left = `${pct}%`; }

    const player = playerSlots.current[currentSlot.current];
    if (player && typeof player.getDuration === "function") {
      const duration = player.getDuration();
      const seekTime = (pct / 100) * duration;
      try {
        player.seekTo(seekTime, true);
        const vidIdx = videoSlots.current[currentSlot.current];
        const videoId = videosRef.current[vidIdx]?.id || videosRef.current[vidIdx];
        if (videoId) videoProgressMap.current[videoId] = seekTime;
      } catch (e) {}
    }
  }, []);

  const handleProgressDragEnd = useCallback((e) => {
    if (!isDraggingProgress.current) return;
    e.stopPropagation(); e.preventDefault();
    isDraggingProgress.current = false;
    const bar = globalProgressBarRef.current;
    const thumb = progressThumbRef.current;
    if (bar) bar.style.transition = "width 0.12s linear";
    if (thumb) thumb.style.transition = "left 0.12s linear";
    if (wasPlayingBeforeScrub.current) {
      const player = playerSlots.current[currentSlot.current];
      if (player && typeof player.playVideo === "function") {
        try {
          player.playVideo();
          const vidIdx = videoSlots.current[currentSlot.current];
          const vidObj = videosRef.current[vidIdx];
          startProgressInterval(currentSlot.current, vidObj?.id || vidObj);
        } catch (e) {}
      }
    }
    wasPlayingBeforeScrub.current = false;
  }, [startProgressInterval]);

  /* ---------- Drag detection helpers ---------- */
  const detectTap = useCallback(() => {
    const delta = Math.abs(dragDeltaY.current);
    const time = Date.now() - dragStartTime.current;
    if (delta < 8 && time < 250) handleTap(currentSlot.current);
  }, [handleTap]);

  const handleDragStart = useCallback((clientY) => {
    if (isDragging.current || isDraggingProgress.current) return;
    isDragging.current = true;
    dragStartTime.current = Date.now();
    touchStartY.current = clientY;
    dragDeltaY.current = 0;
    for (let i = 0; i < 3; i++) {
      const el = slotRefs.current[i];
      if (el) el.style.transition = "none";
    }
  }, []);

  const handleDragMove = useCallback((clientY) => {
    if (!isDragging.current || isDraggingProgress.current) return;
    dragDeltaY.current = clientY - touchStartY.current;
    if (currentIndexRef.current === 0 && dragDeltaY.current > 0) {
      dragDeltaY.current = 0;
      return;
    }
    if (rafId.current == null) {
      rafId.current = requestAnimationFrame(() => {
        applySlotTransformsDirect(dragDeltaY.current);
        rafId.current = null;
      });
    }
  }, [applySlotTransformsDirect]);

  const handleDragEnd = useCallback((clientY) => {
    if (!isDragging.current || isDraggingProgress.current) return;
    const touchEndY = clientY ?? touchStartY.current;
    const delta = touchEndY - touchStartY.current;
    const timePassed = Math.max(1, Date.now() - dragStartTime.current);
    isDragging.current = false;
    dragDeltaY.current = 0;
    performNavigateOrReset(delta, timePassed);
  }, [performNavigateOrReset]);

  /* ---------- Pointer/touch wiring ---------- */
  const onPointerDown = useCallback((e) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const clientY = e.clientY ?? 0;
    handleDragStart(clientY);
    try { e.currentTarget?.setPointerCapture?.(e.pointerId); } catch {}
  }, [handleDragStart]);

  const onPointerMove = useCallback((e) => {
    if (!isDragging.current) return;
    handleDragMove(e.clientY ?? 0);
  }, [handleDragMove]);

  const onPointerUp = useCallback((e) => {
    if (!isDragging.current) return;
    const clientY = e.clientY ?? 0;
    detectTap();
    handleDragEnd(clientY);
  }, [detectTap, handleDragEnd]);

  const onTouchStart = useCallback((e) => {
    const t = e.touches ? e.touches[0] : null;
    const clientY = t ? t.clientY : e.clientY ?? 0;
    handleDragStart(clientY);
  }, [handleDragStart]);

  const onTouchMove = useCallback((e) => {
    if (e.cancelable) e.preventDefault();

    const t = e.touches ? e.touches[0] : null;
    const clientY = t ? t.clientY : e.clientY ?? 0;
    if (!isDragging.current) handleDragStart(clientY);
    else handleDragMove(clientY);
  }, [handleDragStart, handleDragMove]);

  const onTouchEnd = useCallback((e) => {
    if (!isDragging.current) return;
    const clientY = e.changedTouches ? e.changedTouches[0].clientY : 0;
    detectTap();
    handleDragEnd(clientY);
  }, [detectTap, handleDragEnd]);

  /* ---------- Wheel nav ---------- */
  const throttle = (fn, limit) => {
    let last = 0, t = null;
    return (...args) => {
      const now = Date.now();
      const rem = limit - (now - last);
      if (rem <= 0) {
        if (t) { clearTimeout(t); t = null; }
        last = now;
        fn(...args);
      } else if (!t) {
        t = setTimeout(() => {
          last = Date.now();
          t = null;
          fn(...args);
        }, rem);
      }
    };
  };

  const wheelNav = useMemo(() =>
    throttle((e) => {
      if (isDragging.current) return;
      const dir = e.deltaY > 0 ? 1 : -1;
      if (dir === -1 && currentIndexRef.current === 0) return;
      const newIndex = currentIndexRef.current + dir;
      if (newIndex >= 0 && newIndex < videosRef.current.length) navigateToIndex(newIndex);
    }, 250), [navigateToIndex]);

  useEffect(() => {
    const node = containerRef.current || window;
    node.addEventListener("wheel", wheelNav, { passive: true });
    return () => node.removeEventListener("wheel", wheelNav);
  }, [wheelNav]);

  /* ============================
     Initialization (single-run) - critical to avoid infinite loops
     ============================ */
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const storedCategories = getCurrentCategory();
        const isCustomized = !(storedCategories.length === 1 && storedCategories[0] === "all");
        setUserCustomized(isCustomized);

        // Attempt to restore saved queue only when categories match
        const savedQueue = loadVideoQueue();
        const savedIndex = loadCurrentIndex();
        const savedConfig = loadFetchConfig();
        const savedMatchesCategory = savedConfig && Array.isArray(savedConfig.categories)
          ? categoriesEqual(savedConfig.categories, storedCategories)
          : false;

        if (savedQueue && Array.isArray(savedQueue) && savedMatchesCategory) {
          const normalized = savedQueue.map(normalizeEntry).filter(Boolean);
          if (mounted) {
            setVideos(normalized);
            videosRef.current = normalized;
            const restoredIndex = Number.isFinite(savedIndex) && savedIndex >= 0 && savedIndex < normalized.length ? savedIndex : 0;
            videoSlots.current = [restoredIndex, Math.min(restoredIndex + 1, normalized.length - 1), Math.max(0, restoredIndex - 1)];
            currentSlot.current = 0;
            setCurrentIndex(restoredIndex);
            setIsFetching(false);
            // initialize players later in YT-ready effect
            return;
          }
        }

        // If not customized and initial not shown -> show initial 7
        if (!isCustomized && !getInitialShortsShown()) {
          const shuffledInitial = getShuffledInitialShorts();
          const initialShorts = shuffledInitial.map((id) => normalizeEntry(id)).filter(Boolean);
          if (mounted) {
            setVideos(initialShorts);
            videosRef.current = initialShorts;
            saveVideoQueue(initialShorts);
            saveFetchConfig({ categories: ["all"] });
            const savedVideoId = loadCurrentVideoId();
            let restoredIndex = 0;
            if (savedVideoId) {
              const foundIndex = initialShorts.findIndex((v) => v.id === savedVideoId);
              if (foundIndex !== -1) restoredIndex = foundIndex;
            }
            videoSlots.current = [restoredIndex, Math.min(restoredIndex + 1, initialShorts.length - 1), Math.max(0, restoredIndex - 1)];
            currentSlot.current = 0;
            setCurrentIndex(restoredIndex);
            // DO NOT setInitialShortsShown(true) here — leave it false until we append platform shorts.
            setIsFetching(false);
            return;
          }
        }

        // Otherwise fetch platform shorts for stored categories
        const categories = storedCategories;
        const loaded = await loadPlatformShorts({ categories, forceRefresh: false });
        if (mounted) {
          if (loaded && loaded.length > 0) {
            const shuffledInitial = getShuffledInitialShorts();
            const initialIds = new Set(shuffledInitial);
            const filtered = loaded.filter(it => !initialIds.has(it.id));
            const initialShorts = shuffledInitial.map(normalizeEntry).filter(Boolean);
            const final = (!isCustomized) ? [...initialShorts, ...filtered] : filtered;
            setVideos(final);
            videosRef.current = final;
            saveVideoQueue(final);
            saveFetchConfig({ categories });
            setCurrentIndex(0);
            // If we included the initial shorts in 'final', mark them shown (because we've already merged).
            if (!isCustomized) setInitialShortsShown(true);
          } else {
            const shuffledInitial = getShuffledInitialShorts();
            const initialShorts = shuffledInitial.map(normalizeEntry).filter(Boolean);
            setVideos(initialShorts);
            videosRef.current = initialShorts;
            saveVideoQueue(initialShorts);
            saveFetchConfig({ categories: ["all"] });
            setCurrentIndex(0);
          }
          setIsFetching(false);
        }
      } catch (err) {
        if (!mounted) return;
        setError(err?.message || String(err));
        setIsFetching(false);
      }
    })();
    return () => { mounted = false; };
    // Run once
  }, [loadPlatformShorts, getCurrentCategory, categoriesEqual]);

  /* ============================
     YT players init when API ready
     ============================ */
  useEffect(() => {
    if (!apiReady || videos.length === 0 || !isMountedRef.current) return;
    let cancelled = false;
    let attemptCount = 0;
    const maxAttempts = 60;
    const waitUntilAllRefsReady = () => {
      if (cancelled) return;
      attemptCount++;
      const allRefsReady = iframeNodeRefs.current.every((ref) => ref instanceof Element);
      if (allRefsReady) {
        initializePlayers();
      } else if (attemptCount < maxAttempts) {
        requestAnimationFrame(waitUntilAllRefsReady);
      }
    };
    requestAnimationFrame(waitUntilAllRefsReady);
    return () => { cancelled = true; };
  }, [apiReady, videos, initializePlayers]);

  useEffect(() => {
    if (apiReady && videos.length > 0) {
      setTimeout(() => initializePlayers(), 100);
    }
  }, [apiReady, videos, initializePlayers]);

  useEffect(() => {
    return () => {
      destroyPlayers();
    };
  }, [destroyPlayers]);

  /* ============================
     Title fetching effect (visible window)
     ============================ */
  useEffect(() => {
    if (!videos || videos.length === 0) return;
    const windowSize = 5;
    const startIdx = Math.max(0, currentIndex - windowSize);
    const endIdx = Math.min(videos.length - 1, currentIndex + windowSize);
    const visibleVideos = videos.slice(startIdx, endIdx + 1);
    const missingIds = visibleVideos
      .map((v) => v.id)
      .filter((id) => id && !videoTitlesRef.current[id] && !recentlyFetchedIds.current.has(id));
    if (missingIds.length === 0) return;

    let cancelled = false;
    (async () => {
      await fetchTitlesForIds(missingIds);
      if (cancelled) return;
    })();
    return () => { cancelled = true; };
  }, [videos, currentIndex, fetchTitlesForIds]);

  /* ============================
     vitstra-update and storage listeners (explicit refresh handling)
     ============================ */
  useEffect(() => {
    const vitstraUpdateHandler = (ev) => {
      try {
        const detail = ev?.detail || {};
        if (detail.category) {
          const norm = Array.isArray(detail.category) ? detail.category.map(c => String(c).toLowerCase()) : [String(detail.category).toLowerCase()];
          localStorage.setItem("vitstraCategory", JSON.stringify(norm));
        }
      } catch (e) {}
      // Mark customized and initial shown to avoid reverting to initial flow
      setUserCustomized(true);
      setInitialShortsShown(true);
      const shouldForce = ev?.detail?.forceReload === true;
      if (shouldForce) {
        const cats = getCurrentCategory();
        (async () => {
          setIsFetching(true);
          const loaded = await loadPlatformShorts({ categories: cats, forceRefresh: true });
          if (loaded && loaded.length > 0) {
            const shuffledInitial = getShuffledInitialShorts();
            const initialIds = new Set(shuffledInitial);
            const filtered = loaded.filter(it => !initialIds.has(it.id));
            const final = (cats && !(cats.length === 1 && cats[0] === "all")) ? filtered : [...shuffledInitial.map(normalizeEntry).filter(Boolean), ...filtered];
            setVideos(final);
            videosRef.current = final;
            saveVideoQueue(final);
            saveFetchConfig({ categories: cats });
            setCurrentIndex(0);
            if (! (cats && !(cats.length === 1 && cats[0] === "all"))) setInitialShortsShown(true);
          }
          setIsFetching(false);
        })();
        return;
      }
      // non-force: debounce reload
      debounce(() => {
        (async () => {
          setIsFetching(true);
          const cats = getCurrentCategory();
          const loaded = await loadPlatformShorts({ categories: cats, forceRefresh: true });
          if (loaded && loaded.length > 0) {
            const shuffledInitial = getShuffledInitialShorts();
            const initialIds = new Set(shuffledInitial);
            const filtered = loaded.filter(it => !initialIds.has(it.id));
            const final = (cats && !(cats.length === 1 && cats[0] === "all")) ? filtered : [...shuffledInitial.map(normalizeEntry).filter(Boolean), ...filtered];
            setVideos(final);
            videosRef.current = final;
            saveVideoQueue(final);
            saveFetchConfig({ categories: cats });
            setCurrentIndex(0);
            if (! (cats && !(cats.length === 1 && cats[0] === "all"))) setInitialShortsShown(true);
          }
          setIsFetching(false);
        })();
      }, 200)();
    };

    const storageHandler = (e) => {
      if (e.key === "userLanguage" || e.key === "vitstraCategory") {
        setUserCustomized(true);
        setInitialShortsShown(true);
        (async () => {
          setIsFetching(true);
          const cats = getCurrentCategory();
          const loaded = await loadPlatformShorts({ categories: cats, forceRefresh: true });
          if (loaded && loaded.length > 0) {
            const shuffledInitial = getShuffledInitialShorts();
            const initialIds = new Set(shuffledInitial);
            const filtered = loaded.filter(it => !initialIds.has(it.id));
            const final = (cats && !(cats.length === 1 && cats[0] === "all")) ? filtered : [...shuffledInitial.map(normalizeEntry).filter(Boolean), ...filtered];
            setVideos(final);
            videosRef.current = final;
            saveVideoQueue(final);
            saveFetchConfig({ categories: cats });
            setCurrentIndex(0);
            if (! (cats && !(cats.length === 1 && cats[0] === "all"))) setInitialShortsShown(true);
          }
          setIsFetching(false);
        })();
      }
    };

    window.addEventListener("vitstra-update", vitstraUpdateHandler);
    window.addEventListener("storage", storageHandler);

    return () => {
      window.removeEventListener("vitstra-update", vitstraUpdateHandler);
      window.removeEventListener("storage", storageHandler);
    };
  }, [loadPlatformShorts, getCurrentCategory]);

  /* ============================
     Visibility handling (save state)
     ============================ */
  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) {
        pausePlayer(currentSlot.current);
        stopProgressInterval();
        saveCurrentState();
      } else {
        setTimeout(() => {
          const player = playerSlots.current[currentSlot.current];
          if (player && typeof player.getPlayerState === "function") {
            try {
              player.getPlayerState();
              playPlayer(currentSlot.current);
              const vidIndex = videoSlots.current[currentSlot.current];
              if (vidIndex >= 0) startProgressInterval(currentSlot.current, videosRef.current[vidIndex]?.id || videosRef.current[vidIndex]);
            } catch {
              initializePlayers();
            }
          } else {
            initializePlayers();
          }
        }, 100);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [pausePlayer, playPlayer, startProgressInterval, stopProgressInterval, saveCurrentState, initializePlayers]);

  /* ============================
     Footer progress placement
     ============================ */
  useEffect(() => {
    const updateProgressBottom = () => {
      const el = globalProgressRef.current;
      if (!el) return;
      const footer = document.querySelector(".premium-footer");
      const footerHeight = footer ? footer.getBoundingClientRect().height : 72;
      el.style.bottom = `${Math.round(footerHeight + 12)}px`;
    };
    updateProgressBottom();
    window.addEventListener("resize", updateProgressBottom);
    const obs = new MutationObserver(updateProgressBottom);
    const f = document.querySelector(".premium-footer");
    if (f) obs.observe(f, { attributes: true, childList: true, subtree: true });
    return () => {
      window.removeEventListener("resize", updateProgressBottom);
      obs.disconnect();
    };
  }, []);

  /* ============================
     Render
     ============================ */
  if (isFetching) {
    return (
      <div className="short-loading">
        <h2>Loading shorts...</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div className="short-error">
        <h2>Error</h2>
        <p>{error}</p>
      </div>
    );
  }

  if (!videos || videos.length === 0) {
    return (
      <div className="no-shorts-message">
        <h2>No shorts available!</h2>
      </div>
    );
  }

  const portalTarget = (typeof document !== "undefined" && document.getElementById("vitstra-footer-portal")) ||
    (typeof document !== "undefined" && document.body) || null;

  return (
    <div ref={containerRef} className="shorts-container" data-short-current-index={currentIndex}>

      <div
        ref={overlayRef}
        className="drag-capture-layer"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      />

      {!hasUserInteracted && videos.length > 0 && createPortal(
        <div
          className="shorts-first-play-overlay"
          onClick={(e) => {
            e.stopPropagation();
            setHasUserInteracted(true);
            setTimeout(() => handleTap(currentSlot.current), 100);
          }}
        >
          <div className="shorts-first-play-button">
            <svg viewBox="0 0 68 68" width="68" height="68">
              <circle cx="34" cy="34" r="33" fill="rgba(0,0,0,0.6)" stroke="#fff" strokeWidth="2" />
              <path d="M26 22 L26 46 L48 34 Z" fill="#fff" />
            </svg>
          </div>
        </div>, document.body
      )}

      {[0, 1, 2].map((slotIndex) => {
        const mappedVideoIndex = videoSlots.current[slotIndex];
        const videoObj = mappedVideoIndex >= 0 && mappedVideoIndex < videos.length ? videos[mappedVideoIndex] : null;
        const videoId = videoObj?.id || (typeof videoObj === "string" ? videoObj : "dQw4w9WgXcQ");
        const fallbackTitle = videoObj?.title || "Loading title...";
        const realTitle = videoTitles[videoId] || fallbackTitle;
        const relative = (slotIndex - currentSlot.current + 3) % 3;
        const offsetY = relative === 0 ? 0 : (relative === 1 ? 100 : -100);
        const isActive = relative === 0;
        const transform = `translate3d(0, ${offsetY}%, 0)`;
        const thumbShouldShow = thumbVisible.current[slotIndex] && !isActive;

        return (
          <div
            key={slotIndex}
            ref={setSlotRef(slotIndex)}
            className={`short-slot ${isActive ? "active" : "inactive"}`}
            style={{
              transform,
              transition: "transform 420ms cubic-bezier(0.22,1,0.36,1), opacity 220ms linear",
              zIndex: isActive ? 70 : 60,
              pointerEvents: "none",
              opacity: isActive ? 1 : 0.985,
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%"
            }}
          >
            <div className="short-inner">
              <div className="short-video-area">
                <div
                  className={`short-thumb ${thumbShouldShow ? "show" : "hide"}`}
                  style={{ backgroundImage: `url(${videoObj?.thumbnail})` }}
                />
                <div className="short-iframe-wrap">
                  <div
                    ref={setIframeRef(slotIndex)}
                    className={`short-iframe-placeholder ${isActive ? "active" : ""}`}
                    data-videoid={videoId}
                    style={{
                      width: "100%",
                      height: "100%",
                      position: "relative"
                    }}
                  />
                </div>
              </div>
              {isActive && (
                <div className="short-title-above-progress">
                  <div className="short-title-text">
                    {realTitle}
                  </div>
                </div>
              )}
              <div className="short-meta-panel">
                <div className="short-meta-content" />
              </div>
            </div>
          </div>
        );
      })}

      {portalTarget ? createPortal(
        <div
          ref={globalProgressRef}
          className="shorts-global-progress"
          onMouseDown={handleProgressDragStart}
          onMouseMove={handleProgressDragMove}
          onMouseUp={handleProgressDragEnd}
          onMouseLeave={handleProgressDragEnd}
          onTouchStart={handleProgressDragStart}
          onTouchMove={handleProgressDragMove}
          onTouchEnd={handleProgressDragEnd}
          onClick={(e) => e.stopPropagation()}
        >
          <div ref={globalProgressBarRef} className="shorts-global-progress-bar" />
          <div ref={progressThumbRef} className="shorts-global-progress-thumb" />
        </div>,
        portalTarget
      ) : null}
    </div>
  );
}